import { useState, useEffect } from "react";
import { subirDatos, bajarDatos, subirDatosSingle, bajarDatosSingle } from "./syncSupabase";

export const STORAGE_CONFIG = 'alqos_finanzas_config';
export const STORAGE_CUENTAS = 'alqos_plan_cuentas';

export const CONFIG_VACIA = {
  moneda: 'ARS',
  ejercicioFiscalInicio: new Date().getFullYear(),
  cuentaVentasDefault: null,
  cuentaComprasDefault: null,
  cuentaCxCDefault: null,
  cuentaCxPDefault: null,
  cuentaCajaDefault: null,
  ultimoAsiento: 0,
  seedPlanCuentasHecho: false,
};

// Plan de cuentas base — se inserta una sola vez si el plan está vacío
export const PLAN_CUENTAS_BASE = [
  { codigo: '1.0.00', nombre: 'ACTIVO',                    tipo: 'activo' },
  { codigo: '1.1.00', nombre: 'Activo Corriente',          tipo: 'activo' },
  { codigo: '1.1.01', nombre: 'Caja y Bancos',             tipo: 'activo' },
  { codigo: '1.1.02', nombre: 'Cuentas por Cobrar',        tipo: 'activo' },
  { codigo: '1.1.03', nombre: 'Stock / Inventarios',       tipo: 'activo' },
  { codigo: '1.2.00', nombre: 'Activo No Corriente',       tipo: 'activo' },
  { codigo: '2.0.00', nombre: 'PASIVO',                    tipo: 'pasivo' },
  { codigo: '2.1.00', nombre: 'Pasivo Corriente',          tipo: 'pasivo' },
  { codigo: '2.1.01', nombre: 'Cuentas por Pagar',         tipo: 'pasivo' },
  { codigo: '2.1.02', nombre: 'Obligaciones Tributarias',  tipo: 'pasivo' },
  { codigo: '3.0.00', nombre: 'PATRIMONIO',                tipo: 'patrimonio' },
  { codigo: '3.1.00', nombre: 'Capital Social',            tipo: 'patrimonio' },
  { codigo: '3.2.00', nombre: 'Resultado del Ejercicio',   tipo: 'patrimonio' },
  { codigo: '4.0.00', nombre: 'INGRESOS',                  tipo: 'ingreso' },
  { codigo: '4.1.00', nombre: 'Ventas',                    tipo: 'ingreso' },
  { codigo: '4.2.00', nombre: 'Otros Ingresos',            tipo: 'ingreso' },
  { codigo: '5.0.00', nombre: 'EGRESOS',                   tipo: 'egreso' },
  { codigo: '5.1.00', nombre: 'Costo de Ventas',           tipo: 'egreso' },
  { codigo: '5.2.00', nombre: 'Gastos Administrativos',    tipo: 'egreso' },
  { codigo: '5.3.00', nombre: 'Gastos de Producción',      tipo: 'egreso' },
];

// ─── Helper exportado: asegura que exista el plan de cuentas base ──
// Idempotente: solo siembra si el plan de cuentas está vacío. Usarlo al
// entrar a cualquier pantalla de Finanzas (Finanzas.js, PlanCuentas.js, etc).
export async function asegurarPlanCuentasBase() {
  try {
    await bajarDatos(STORAGE_CUENTAS);
    const actuales = JSON.parse(localStorage.getItem(STORAGE_CUENTAS) || '[]');
    if (actuales.length > 0) return actuales;

    const base = new Date();
    const nuevas = PLAN_CUENTAS_BASE.map((c, i) => ({
      ...c,
      id: Math.floor(base.getTime()) + i,
      saldo: 0,
      parentId: null,
      activo: true,
    }));
    localStorage.setItem(STORAGE_CUENTAS, JSON.stringify(nuevas));
    subirDatos(STORAGE_CUENTAS);
    return nuevas;
  } catch (e) {
    console.error('Error sembrando plan de cuentas base:', e);
    return [];
  }
}

export default function FinanzasConfig({ onVolver, oscuro, usuario }) {
  const [config, setConfig] = useState(CONFIG_VACIA);
  const [cuentas, setCuentas] = useState([]);
  const [guardado, setGuardado] = useState(false);

  useEffect(() => {
    (async () => {
      await bajarDatosSingle(STORAGE_CONFIG, 'finanzas_config');
      const raw = localStorage.getItem(STORAGE_CONFIG);
      if (raw) { try { setConfig({ ...CONFIG_VACIA, ...JSON.parse(raw) }); } catch {} }
      const planCuentas = await asegurarPlanCuentasBase();
      setCuentas(planCuentas);
    })();
  }, []);

  const set = (k, v) => setConfig(c => ({ ...c, [k]: v }));

  const guardar = () => {
    localStorage.setItem(STORAGE_CONFIG, JSON.stringify(config));
    subirDatosSingle(STORAGE_CONFIG, 'finanzas_config');
    setGuardado(true);
    setTimeout(() => setGuardado(false), 2500);
  };

  const inp = { width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'Inter, sans-serif' };
  const lbl = { fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px', display: 'block' };

  const cuentasCaja = cuentas.filter(c => c.codigo?.startsWith('1.1.01'));
  const cuentasCxC = cuentas.filter(c => c.codigo?.startsWith('1.1.02'));
  const cuentasCxP = cuentas.filter(c => c.codigo?.startsWith('2.1.01'));
  const cuentasVentas = cuentas.filter(c => c.tipo === 'ingreso');
  const cuentasCompras = cuentas.filter(c => c.tipo === 'egreso');

  return (
    <div style={{ padding: '24px', fontFamily: 'Inter, sans-serif', background: '#E8E9F6', minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
        <button onClick={onVolver} style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', color: '#6B7280' }}>← Menú</button>
        <span style={{ fontSize: '22px' }}>⚙️</span>
        <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1C3FAA', margin: 0 }}>Configuración de Finanzas</h1>
      </div>

      <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #E5E7EB', padding: '24px', maxWidth: '520px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div>
          <label style={lbl}>Moneda</label>
          <input style={inp} value={config.moneda} onChange={e => set('moneda', e.target.value)} placeholder="ARS" />
        </div>
        <div>
          <label style={lbl}>Ejercicio fiscal — año de inicio</label>
          <input style={inp} type="number" value={config.ejercicioFiscalInicio} onChange={e => set('ejercicioFiscalInicio', Number(e.target.value))} />
        </div>
        <div>
          <label style={lbl}>Cuenta de Ventas por defecto</label>
          <select style={inp} value={config.cuentaVentasDefault || ''} onChange={e => set('cuentaVentasDefault', e.target.value ? Number(e.target.value) : null)}>
            <option value="">— Seleccionar —</option>
            {cuentasVentas.map(c => <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Cuenta de Compras por defecto (egreso)</label>
          <select style={inp} value={config.cuentaComprasDefault || ''} onChange={e => set('cuentaComprasDefault', e.target.value ? Number(e.target.value) : null)}>
            <option value="">— Seleccionar —</option>
            {cuentasCompras.map(c => <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Cuenta de CxC por defecto</label>
          <select style={inp} value={config.cuentaCxCDefault || ''} onChange={e => set('cuentaCxCDefault', e.target.value ? Number(e.target.value) : null)}>
            <option value="">— Seleccionar —</option>
            {cuentasCxC.map(c => <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Cuenta de CxP por defecto</label>
          <select style={inp} value={config.cuentaCxPDefault || ''} onChange={e => set('cuentaCxPDefault', e.target.value ? Number(e.target.value) : null)}>
            <option value="">— Seleccionar —</option>
            {cuentasCxP.map(c => <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Cuenta de Caja/Bancos por defecto</label>
          <select style={inp} value={config.cuentaCajaDefault || ''} onChange={e => set('cuentaCajaDefault', e.target.value ? Number(e.target.value) : null)}>
            <option value="">— Seleccionar —</option>
            {cuentasCaja.map(c => <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>)}
          </select>
        </div>

        <button onClick={guardar} style={{ marginTop: '10px', padding: '10px', borderRadius: '10px', border: 'none', background: '#1C3FAA', color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
          {guardado ? '✅ Guardado' : 'Guardar configuración'}
        </button>
      </div>
    </div>
  );
}
